/**
 * NgwEditorCommand — `IEditorCommand` 의 NgwEditor 본 구현 (PR-N3).
 *
 * 타입 안전한 명령 디스패처. `EditorCommandName` 의 27 개 중립 명령을 받아 `NGW_COMMAND_MAP`
 * 으로 NgwEditor 의 `executeCommand` 명령 이름에 매핑 후 `handle.executeCommand(...)` 호출.
 *
 * ### 특수 처리 명령
 *
 * - `link` — `adapter.state.requestLinkInfo()` 위임 (사용자 결정 (a), PR-N6 의 selection wiring 후 실 동작).
 * - `alignLeft` / `alignCenter` / `alignRight` / `alignJustify`
 *   — NgwEditor 는 단일 `setTextAlign(align)` + literal union (D-N5). cmd 에서 align 값 추출.
 * - `insertTable` — `handle.executeCommand('insertTable', spec)` 직접 호출. TinyMCE 의 `setTableCellWidth`
 *   선행은 NgwEditor 에 동등 표면 없음 — 사용자 결정 (a) 로 단순 호출.
 * - `fontFamily` — `setFontFamily` 호출 후 `getFeature('font-formatting')?.getCurrentFontFamily?.()`
 *   로 사후 조회 후 string 반환. 조회 실패 시 입력 name 그대로 반환 (사용자 결정 (a)).
 * - `horizontalRule` — `insertHorizontalRule` 호출 후 `opts.style` 을 NgwEditor variant 로 매핑한
 *   `changeHorizontalRuleVariant` 후속 호출 (사용자 결정 (a)).
 * - `removeFormat` — `extensions/removeFormatExtension.ts` 가 등록한 동명 명령 호출
 *   (일반 매핑 경로). map 이 `'removeFormat'` 으로 정의됨.
 * - `codeView` — NgwEditor 0.2.10 미지원. NoOp + 1회 로그 (D-N4, 사용자 결정 (a)).
 * - `directionLTR` / `directionRTL` — PR-N2 의 `NgwEditorDirection.setRTL()` 위임 (사용자 결정 (a)).
 *
 * ### 안전성
 *
 * - init 전 호출 (handle == null) 은 silent 반환. fontFamily 만 입력 name 반환 (시그니처 보존).
 * - args 의 type guard 는 `EditorCommandArgs<K>` 를 통한 tuple 추출로 처리 — `any` 0건.
 */

import type {
  EditorCommandArgs,
  EditorCommandName,
  EditorCommandResult,
  IEditorCommand,
} from '../IEditorCommand'
import { NGW_COMMAND_MAP } from './NGW_COMMAND_MAP'
import type { NgwEditorAdapter } from './NgwEditorAdapter'

/** TinyMCE 의 `style` 키 → NgwEditor 의 horizontal-rule variant 매핑. */
type NgwHrVariant = 'solid' | 'wavy' | 'diamond' | 'dotted' | 'solid-thin' | 'solid-thick'

/** ProseMirror Selection API 에서 textAlign 인자로 받는 4 literal. */
type TextAlignValue = 'left' | 'center' | 'right' | 'justify'

const ALIGN_VALUE_BY_CMD: Record<
  'alignLeft' | 'alignCenter' | 'alignRight' | 'alignJustify',
  TextAlignValue
> = {
  alignLeft: 'left',
  alignCenter: 'center',
  alignRight: 'right',
  alignJustify: 'justify',
}

/**
 * TinyMCE 의 horizontalRule style 키워드 → NgwEditor variant 이름 매핑.
 *
 * TinyMCE 측 호출자는 'solid' / 'dotted' / 'dashed' / 'double' 등 CSS border-style 키워드를 전달한다.
 * NgwEditor 의 horizontal-rule 명령은 위 6 variant 만 인식하므로 미지원 키워드는 'solid' 로 fallback.
 */
const HR_VARIANT_MAP: Record<string, NgwHrVariant> = {
  solid: 'solid',
  wavy: 'wavy',
  diamond: 'diamond',
  dotted: 'dotted',
  'solid-thin': 'solid-thin',
  'solid-thick': 'solid-thick',
  // 시각 호환 fallback — NgwEditor 미지원 keyword 가 들어와도 동작 보장.
  dashed: 'dotted',
  double: 'solid-thick',
  groove: 'solid',
  ridge: 'solid',
}

/** `getFeature('font-formatting')` 의 사후 조회 표면 (ambient 미정의 — 사용 패턴 최소화 타입). */
interface FontFormattingFeatureLike {
  getCurrentFontFamily?: () => string | null | undefined
}

const CODE_VIEW_LOG_TAG = '[NgwEditorCommand] codeView — NgwEditor 0.2.10 미지원, NoOp (D-N4)'

export class NgwEditorCommand implements IEditorCommand {
  private readonly adapter: NgwEditorAdapter
  private codeViewLogEmitted = false

  constructor(adapter: NgwEditorAdapter) {
    this.adapter = adapter
  }

  run<K extends EditorCommandName>(cmd: K, ...args: EditorCommandArgs<K>): EditorCommandResult<K> {
    // 특수 처리: link → state.requestLinkInfo 위임 (handle 유무와 무관 — state 가 자체 가드).
    if (cmd === 'link') {
      this.adapter.state.requestLinkInfo()
      return undefined as EditorCommandResult<K>
    }

    // 특수 처리: direction — DOM dir attribute 토글 (PR-N2 sub-adapter 위임).
    if (cmd === 'directionLTR' || cmd === 'directionRTL') {
      this.adapter.direction.setRTL(cmd === 'directionRTL')
      return undefined as EditorCommandResult<K>
    }

    // 특수 처리: codeView — NgwEditor 미지원, NoOp + 첫 호출 1회 로그.
    if (cmd === 'codeView') {
      if (!this.codeViewLogEmitted) {
        this.codeViewLogEmitted = true
        console.warn(CODE_VIEW_LOG_TAG)
      }
      return undefined as EditorCommandResult<K>
    }

    const handle = this.adapter.lifecycle.getHandle()
    if (handle == null) {
      // init 전 호출 — fontFamily 만 입력 name 반환, 그 외는 silent.
      if (cmd === 'fontFamily') {
        const [name] = args as EditorCommandArgs<'fontFamily'>
        return name as EditorCommandResult<K>
      }
      return undefined as EditorCommandResult<K>
    }

    // 특수 처리: align* — NgwEditor 의 단일 setTextAlign + literal union (D-N5).
    if (
      cmd === 'alignLeft' ||
      cmd === 'alignCenter' ||
      cmd === 'alignRight' ||
      cmd === 'alignJustify'
    ) {
      handle.executeCommand('setTextAlign', ALIGN_VALUE_BY_CMD[cmd])
      return undefined as EditorCommandResult<K>
    }

    // 특수 처리: insertTable — { rows, columns } 객체 직접 전달.
    if (cmd === 'insertTable') {
      const [spec] = args as EditorCommandArgs<'insertTable'>
      handle.executeCommand('insertTable', spec)
      return undefined as EditorCommandResult<K>
    }

    // 특수 처리: fontFamily — setFontFamily 호출 후 사후 조회로 적용 결과 반환.
    if (cmd === 'fontFamily') {
      const [name] = args as EditorCommandArgs<'fontFamily'>
      handle.executeCommand('setFontFamily', name)
      const feature = handle.getFeature('font-formatting') as FontFormattingFeatureLike | undefined
      const current = feature?.getCurrentFontFamily?.()
      const resolved = typeof current === 'string' && current.length > 0 ? current : name
      return resolved as EditorCommandResult<K>
    }

    // 특수 처리: horizontalRule — insertHorizontalRule + (선택) changeHorizontalRuleVariant.
    if (cmd === 'horizontalRule') {
      const [opts] = args as EditorCommandArgs<'horizontalRule'>
      handle.executeCommand('insertHorizontalRule')
      const style = opts?.style
      if (style != null) {
        const variant = HR_VARIANT_MAP[style] ?? 'solid'
        handle.executeCommand('changeHorizontalRuleVariant', variant)
      }
      return undefined as EditorCommandResult<K>
    }

    // 그 외 단순 매핑: NGW_COMMAND_MAP 적용 후 executeCommand 직접 호출.
    const mapped = NGW_COMMAND_MAP[cmd]
    if (mapped == null) {
      // 본 시점 도달은 매핑표/특수 분기 불일치 → 컴파일러로 잡힐 dead-path.
      throw new Error(`NgwEditorCommand.run: no NgwEditor mapping for '${cmd}'`)
    }

    // tuple 첫 인자를 value 로 전달 (없으면 undefined). args 의 element 타입은 cmd 별 다르므로
    // unknown[] 로 좁힌 후 0번째만 추출 — `any` 회피.
    const value = (args as unknown[])[0]
    if (value === undefined) {
      handle.executeCommand(mapped)
    } else {
      handle.executeCommand(mapped, value)
    }
    return undefined as EditorCommandResult<K>
  }
}
